import { InfoOutlined } from "@mui/icons-material";
import { Alert, Box, Link, Typography } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import Image from "next/image";
import aia_example from "@/assets/images/aia_example.png";
import { useUserStore } from "@/store/useUserStore";
import { ATTRIBUTION_NAMES } from "@/constants/User";
import { hasAttribution } from "@/utils/permissions";
import { ROUTES } from "@/constants/Routes";
import { CustomTooltip } from "@/components/CustomTooltip";

export function AIAManagement() {
	const canManage = useUserStore(
		({ attributions, isAdmin }) =>
			hasAttribution(attributions ?? [], ATTRIBUTION_NAMES.SOCIO_JURIDICO) || isAdmin
	);

	return (
		<>
			<Alert severity='warning'>
				Somente administradores e especialistas sócio-jurídicos podem cadastrar
				novas AIAs
			</Alert>

			<Typography variant='body2'>
				O cadastro de uma nova AIA{" "}
				<span style={{ fontWeight: "bold" }}>não</span> requer a aprovação do
				Comitê de Avaliação. Portanto, ao cadastrar uma nova AIA, já é possível
				cadastrar novos questionários para essa AIA.
			</Typography>

			<Box>
				<Image
					loading='lazy'
					height={180}
					placeholder='blur'
					src={aia_example}
					alt='exemplo de AIA'
				/>
				<Typography component={"p"} variant='caption' sx={{ paddingLeft: 0.5 }}>
					Exemplo de AIA (na imagem Direito do Consumidor) com uma pergunta de
					enquadramento, definições relacionadas e somente uma versão de
					questionário publicada
				</Typography>
			</Box>

			<Box>
				<Typography variant='body2'>
					As AIAs são compostas pelas seguintes propriedades:
				</Typography>
				<ul
					style={{
						margin: 0,
						paddingLeft: "1.5rem",
						paddingTop: "0.2rem",
						display: "flex",
						flexDirection: "column",
						gap: 3,
					}}
				>
					<li>
						<Typography variant='body2'>Título</Typography>
					</li>
					<li>
						<Typography variant='body2'>Descrição</Typography>
					</li>
					<li>
						<Box sx={{ display: "flex", gap: 0.6 }}>
							<Typography variant='body2' sx={{ display: "inline-flex" }}>
								Perguntas de enquadramento
							</Typography>

							<Box sx={{ display: "inline-flex", height: "100%" }}>
								<CustomTooltip
									title={
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												gap: 1,
												maxWidth: 240,
											}}
										>
											<Typography variant='body2'>
												Definem se a aplicação deve responder a AIA
											</Typography>
											<Typography variant='body2' fontSize={"0.7rem"}>
												Veja o guia das perguntas de enquadramento para maiores
												detalhes
											</Typography>
										</Box>
									}
									placement='bottom-end'
									offsetX={0}
									offsetY={-10}
								>
									<InfoOutlined
										fontSize='small'
										sx={{
											color: blueGrey[500],
										}}
									/>
								</CustomTooltip>
							</Box>
						</Box>
					</li>
				</ul>
			</Box>

			<Typography variant='body2'>
				Na visão do sistema, as AIAs podem ser entendidas como "repositórios" de
				questionários que visam determinado tema. Em outras palavras,
				questionários são cadastrados dentro das AIAs e conforme as necessidades
				evoluem, novas versões de questionários podem ser cadastradas.
			</Typography>

			<Box>
				<Typography variant='body2'>
					Para o gerenciamento correto das AIAs, algumas normas devem ser
					atentidas:
				</Typography>
				<ul
					style={{
						margin: 0,
						paddingLeft: "1.5rem",
						paddingTop: "0.2rem",
						display: "flex",
						flexDirection: "column",
						gap: 3,
					}}
				>
					<li>
						<Typography variant='body2'>AIAs devem ter nomes únicos</Typography>
					</li>
					<li>
						<Typography variant='body2'>Deve conter descrição</Typography>
					</li>
					<li>
						<Typography variant='body2'>
							Deve haver pelo menos uma pergunta de enquadramento e no máximo
							duas
						</Typography>
					</li>
					<li>
						<Typography variant='body2'>
							Perguntas de enquadramento não podem ter enunciados idênticos
						</Typography>
					</li>
				</ul>
			</Box>
			<Typography variant='body2'>
				As AIAs podem ser{" "}
				{canManage ? (
					<Link href={ROUTES.AIA.REGISTER}>cadastradas</Link>
				) : (
					<span style={{ fontWeight: 600 }}>cadastradas</span>
				)}{" "}
				e{" "}
				{canManage ? (
					<Link href={ROUTES.AIA.EDIT}>alteradas</Link>
				) : (
					<span style={{ fontWeight: 600 }}>alteradas</span>
				)}
				. Portanto, elas <span style={{ fontWeight: 600 }}>não</span> podem ser
				removidas, por tal razão recomenda-se a conferição da real necessidade
				da nova AIA a ser inserida no sistema antes do ato de registro.
			</Typography>

			<Typography variant='body2'>
				Alterar os dados de uma AIA não impacta nos questionários que estão
				vinculados a ela. Exceto os questionários, os dados passíveis de edição
				são os mesmos que a compõem (ver primeira lista).
			</Typography>
		</>
	);
}
